import { ReactElement, memo } from "react";
import { ReleaseContentCardProps } from "../../types";
import { ReleaseGridCard } from "./ReleaseGridCard";
import { ReleaseHorizontalCard } from "./ReleaseHorizontalCard";
import { ReleaseListCard } from "./ReleaseListCard";
import { useNavigateToReaderWithContext } from "../../screens/reader/hooks/useNavigateToReaderWithContext";
import { Link } from "@chakra-ui/react";
import { useSlideInMenu } from "../slideInMenu/hooks/useSlideInMenu";
import { useUserData } from "../../hooks/useUserData";
import { trackRecsysCardItemClick } from "../../analytics/recsys/tracking";
import { applyNavigationAnalyticsAttribution } from "../../analytics/params/navigationContextAttribution";

export const ReleaseCard = memo(
  (props: ReleaseContentCardProps) => {
    const mode = props.layout.style.mode;

    const { userData } = useUserData();
    const userIsGold = userData?.gold;
    const { openReleaseInfoMenu } = useSlideInMenu();
    const { navigationContext } = useNavigateToReaderWithContext({
      releaseToNavigate: props.content,
      cardPosition: props.index,
      sectionPosition: props.sectionIndex,
      section: props.section,
      area: props.area,
    });

    const isFree = props.content.is_free;

    const unlockedByGold = userIsGold && !props.content.premium_only;

    const isLocked =
      !isFree &&
      !props.content.release_user_purchase &&
      (!userIsGold || props.content.premium_only);

    const wasPurchased =
      !!props.content.release_user_purchase || unlockedByGold;

    const cards: { [key: string]: ReactElement } = {
      grid: (
        <ReleaseGridCard
          {...props}
          isLocked={isLocked}
          wasPurchased={wasPurchased}
          showPurchasedBadge={props.showPurchasedBadge}
          goldUnlocked={unlockedByGold}
        />
      ),
      horizontal: (
        <ReleaseHorizontalCard
          {...props}
          isLocked={isLocked}
          wasPurchased={wasPurchased}
          showPurchasedBadge={props.showPurchasedBadge}
          goldUnlocked={unlockedByGold}
        />
      ),
      list: (
        <ReleaseListCard
          {...props}
          isLocked={isLocked}
          wasPurchased={wasPurchased}
          showPurchasedBadge={props.showPurchasedBadge}
          goldUnlocked={unlockedByGold}
        />
      ),
    };

    if (cards[mode]) {
      return (
        <Link
          id={`release-${props.content.id}`}
          draggable={false}
          href={props.content.url}
          width={props.fullWidth ? "100%" : "auto"}
          onClick={(e) => {
            e.preventDefault();
            const impressionId =
              props.section && props.sectionIndex !== undefined
                ? trackRecsysCardItemClick({
                    section: props.section,
                    sectionIndex: props.sectionIndex,
                    item: props.content,
                    itemIndex: props.trackingIndex ?? props.index ?? 0,
                  })
                : undefined;
            if (props.dontOpenInfo) return;
            if (!props.content.key) return;

            openReleaseInfoMenu(
              props.content.key,
              props.content.comic_slug ?? props.content.comic?.slug,
              applyNavigationAnalyticsAttribution(navigationContext, {
                impressionId,
              }),
            );
          }}
        >
          {cards[mode]}
        </Link>
      );
    }

    console.warn("Unable to determine layout mode for ReleaseCard");
    return null;
  },
  (prevProps, nextProps) => {
    // Only re-render if primitive values or IDs change
    // Note: userData.gold comes from useUserData hook, which will trigger re-render via React Query
    // when user purchases gold, so we don't need to compare it in memo
    return (
      prevProps.content.id === nextProps.content.id &&
      prevProps.content.release_user_purchase ===
        nextProps.content.release_user_purchase &&
      prevProps.content.premium_only === nextProps.content.premium_only &&
      prevProps.content.is_free === nextProps.content.is_free &&
      prevProps.content.ppb_price_point === nextProps.content.ppb_price_point &&
      prevProps.content.ppb_sale_price_point ===
        nextProps.content.ppb_sale_price_point &&
      prevProps.content.ppb_gold_price_point ===
        nextProps.content.ppb_gold_price_point &&
      prevProps.content.ppb_gold_sale_price_point ===
        nextProps.content.ppb_gold_sale_price_point &&
      prevProps.layout.style.mode === nextProps.layout.style.mode &&
      prevProps.layout.style.size === nextProps.layout.style.size &&
      prevProps.layout.display === nextProps.layout.display &&
      prevProps.section?.source_type === nextProps.section?.source_type &&
      prevProps.fullWidth === nextProps.fullWidth &&
      prevProps.dontOpenInfo === nextProps.dontOpenInfo &&
      prevProps.index === nextProps.index &&
      prevProps.sectionIndex === nextProps.sectionIndex
    );
  },
);

ReleaseCard.displayName = "ReleaseCard";
